import * as React from 'react';
import { compose } from 'recompose';
import { skin, OemProps } from 'ui/styles/skin';
import { PersistProps } from './persist';

import './SplitView.css';

const splitterSize = 6;

export type State = {
    mouseDown: boolean;
    resizing: boolean;
    value: number;
};

export type InternalProps = {
    orientation: 'horizontal' | 'vertical';
};

export type OwnProps = {
    id?: string;
    children: React.ReactElement<{}>[];
    min1?: number;
    min2?: number;
    onResizeStart?: () => void;
};

export type AllProps = OwnProps & InternalProps & OemProps & PersistProps<number>;

export class RawSplitView extends React.Component<AllProps, State> {
    view: HTMLDivElement | null;
    hv: 'h' | 'v';
    whProp: 'width' | 'height';
    ltProp: 'left' | 'top';

    constructor(props: AllProps) {
        super(props);
        this.view = null;
        this.state = {
            mouseDown: false,
            resizing: false,
            value: parseFloat(this.props.value || '.5'),
        };

        // Assign these for convenience so we don't need to keep evaluating the orientation
        this.hv = this.props.orientation === 'horizontal' ? 'h' : 'v';
        this.whProp = this.props.orientation === 'horizontal' ? 'width' : 'height';
        this.ltProp = this.props.orientation === 'horizontal' ? 'left' : 'top';
    }

    // TODO: on resize, may need to reposition the splitter bar

    onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        this.setState({ mouseDown: true });
    }

    onMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
        if (this.state.mouseDown) {
            this.setState({
                mouseDown: false,
                resizing: false,
            });
            
            // Now that the user has stopped moving the splitter, report the changed value
            const { onChange } = this.props;
            if (onChange) {
                onChange(this.state.value);
            }
        }
    }

    calculateOffset(element: HTMLElement): { x: number; y: number } {
        if (!element) {
            return { x: 0, y: 0 };
        }
        const parentOffset = this.calculateOffset(element.offsetParent as HTMLElement);
        return {
            x: parentOffset.x + (element.offsetLeft || 0),
            y: parentOffset.y + (element.offsetTop || 0),
        };
    }

    onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!this.view) return;
        if (this.state.mouseDown) {
            if (!this.state.resizing && this.props.onResizeStart) {
                this.props.onResizeStart();
            }

            const { min1 = 0, min2 = 0 } = this.props;
            const offset = this.calculateOffset(e.target as HTMLElement);

            if (this.props.orientation === 'horizontal') {
                const minValue = Math.max(min1, splitterSize / 2) / this.view.clientWidth;
                const maxValue = Math.min(this.view.clientWidth - min2, this.view.clientWidth - splitterSize / 2) / this.view.clientWidth;
                const value = Math.max(minValue, Math.min((e.clientX - offset.x) / this.view.clientWidth, maxValue));
                this.setState({ value });
            }
            else {
                const minValue = Math.max(min1, splitterSize / 2) / this.view.clientHeight;
                const maxValue = Math.min(this.view.clientHeight - min2, this.view.clientHeight - splitterSize / 2) / this.view.clientHeight;
                const value = Math.max(minValue, Math.min((e.clientY - offset.y) / this.view.clientHeight, maxValue));
                this.setState({ value });
            }
        }
    }

    calculateHostStyle = (value: number, isFirst: boolean) => {
        if (!isFirst) {
            value = 1 - value;
        }
        return {
            [this.whProp]: `calc(${value * 100}% - ${splitterSize / 2}px)`,
            [this.ltProp]: isFirst ? '0' : `calc(${(1 - value) * 100}% + ${splitterSize / 2}px)`,
        };
    }

    calculateSplitterStyle = (value: number) => ({
        [this.whProp]: `${splitterSize}px`,
        [this.ltProp]: `calc(${value * 100}% - ${splitterSize / 2}px)`,
    })

    render() {
        const { id, children, styles } = this.props;
        const [ first, second ] = children;
        const { value, mouseDown } = this.state;
        return (
            <div
                id={ id }
                className={ `${this.hv}split-view ${mouseDown ? 'resizing' : ''}` }
                onMouseUpCapture={ this.onMouseUp }
                onMouseMove={ this.onMouseMove }
                ref={ _ => this.view = _ }
            >
                <div className={ `${this.hv}split-host1` } style={ this.calculateHostStyle(value, true) }>
                    { first }
                </div>
                <div className={ `${this.hv}split-host2` } style={ this.calculateHostStyle(value, false) }>
                    { second }
                </div>
                <div
                    className={ `${this.hv}split-splitter` }
                    style={ {
                        ...styles.Splitter,
                        ...this.calculateSplitterStyle(value),
                    } }
                    onMouseDown={ this.onMouseDown }
                />
            </div>
        );
    }
}


const SplitView = compose<AllProps, OwnProps & InternalProps & PersistProps<number>>(
    skin,
)(RawSplitView);

export const HorizontalSplitView = ({ children, ...otherProps }: OwnProps & PersistProps<number>) =>
    <SplitView { ...otherProps } orientation={ 'horizontal' }>
        { children }
    </SplitView>
;

export const VerticalSplitView = ({ children, ...otherProps }: OwnProps & PersistProps<number>) =>
    <SplitView { ...otherProps } orientation={ 'vertical' }>
        { children }
    </SplitView>
;



// WEBPACK FOOTER //
// ./src/ui/shared/SplitView.tsx